import React from 'react';
import { ConsultarSinistroPTResult } from '../types';
import { 
  ArrowLeft, 
  AlertOctagon,
  Car,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  Info
} from 'lucide-react';

interface ConsultaSinistroPTResultProps {
  data: ConsultarSinistroPTResult;
  onBack: () => void;
}

const ConsultaSinistroPTResult: React.FC<ConsultaSinistroPTResultProps> = ({ data, onBack }) => {
  const getStatusInfo = () => {
    switch (data.status) {
      case 'sim':
        return {
          icon: XCircle,
          title: 'Sinistro com Perda Total Encontrado',
          subtitle: 'Este veículo possui registro de indenização integral',
          bgGradient: 'from-red-600 to-rose-600',
          cardBg: 'bg-red-500/10',
          cardBorder: 'border-red-500/30',
          iconColor: 'text-red-400',
          badgeBg: 'bg-red-500/20',
          badgeText: 'text-red-400',
        };
      case 'nao':
        return {
          icon: CheckCircle,
          title: 'Nenhum Sinistro com Perda Total',
          subtitle: 'Não há registro de indenização integral para este veículo',
          bgGradient: 'from-green-600 to-emerald-600',
          cardBg: 'bg-green-500/10',
          cardBorder: 'border-green-500/30',
          iconColor: 'text-green-400',
          badgeBg: 'bg-green-500/20',
          badgeText: 'text-green-400',
        };
      case 'indisponivel':
      default:
        return {
          icon: AlertTriangle,
          title: 'Informação Indisponível',
          subtitle: 'Não foi possível verificar o registro neste momento',
          bgGradient: 'from-amber-600 to-orange-600',
          cardBg: 'bg-amber-500/10',
          cardBorder: 'border-amber-500/30',
          iconColor: 'text-amber-400',
          badgeBg: 'bg-amber-500/20',
          badgeText: 'text-amber-400',
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-${data.status === 'sim' ? 'red' : data.status === 'nao' ? 'green' : 'amber'}-950/10 to-slate-950`}>
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Header Banner */}
          <div className={`bg-gradient-to-r ${statusInfo.bgGradient} p-6`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <AlertOctagon className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">Sinistro com Perda Total</h1>
                <p className="text-white/80 text-sm mt-1">
                  Verificação de indenização integral
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800/50 rounded-xl">
                <Car className="w-6 h-6 text-slate-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-mono font-bold text-white tracking-wider">{data.placa}</p>
                <p className="text-sm text-slate-400">Placa consultada</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Consultado em</p>
                <p className="text-sm text-slate-400">{data.dataConsulta}</p>
              </div>
            </div>
          </div>

          {/* Result Section */}
          <div className="p-6 space-y-6">
            {/* Main Status Card */}
            <div className={`p-8 rounded-2xl border ${statusInfo.cardBg} ${statusInfo.cardBorder} text-center`}>
              <div className="flex justify-center mb-4">
                <div className={`p-4 rounded-full ${statusInfo.cardBg}`}>
                  <StatusIcon className={`w-16 h-16 ${statusInfo.iconColor}`} />
                </div>
              </div>
              <h2 className={`text-2xl font-bold ${statusInfo.iconColor} mb-2`}>
                {statusInfo.title}
              </h2>
              <p className="text-slate-400">
                {statusInfo.subtitle}
              </p>
              
              {/* Badge */}
              <div className="mt-6">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusInfo.badgeBg} ${statusInfo.badgeText} text-sm font-medium`}>
                  {data.status === 'sim' && <ShieldAlert className="w-4 h-4" />}
                  {data.status === 'nao' && <ShieldCheck className="w-4 h-4" />}
                  {data.status === 'indisponivel' && <AlertTriangle className="w-4 h-4" />}
                  {data.status === 'sim' ? 'Atenção Recomendada' : 
                   data.status === 'nao' ? 'Veículo Verificado' : 
                   'Verificar Posteriormente'}
                </span>
              </div>
            </div>

            {/* Registry Detail (if exists) */}
            {data.registro && (
              <div className="p-5 bg-slate-800/30 rounded-2xl border border-slate-700/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Detalhes do Registro</p>
                    <p className="text-white font-medium">{data.registro}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <AlertOctagon className="w-4 h-4" />
                  <span>Status</span>
                </div>
                <p className={`font-medium ${statusInfo.iconColor}`}>
                  {data.status === 'sim' ? 'Possui Registro' : 
                   data.status === 'nao' ? 'Sem Registro' : 
                   'Indisponível'}
                </p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Data da Consulta</span>
                </div>
                <p className="text-white font-medium">
                  {new Date(data.dataConsulta.replace(' ', 'T')).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            {/* Explanation */}
            <div className="p-5 bg-slate-800/20 rounded-2xl border border-slate-700/30">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                O que significa Perda Total?
              </h3>
              <p className="text-sm text-slate-400">
                Um veículo com registro de <strong className="text-slate-300">Perda Total</strong> (ou Indenização Integral) 
                é aquele que foi declarado como sinistrado pelas seguradoras após um acidente ou evento 
                onde o custo do reparo excedeu um percentual significativo do valor do veículo 
                (geralmente 75% ou mais). Isso pode indicar danos estruturais graves.
              </p>
            </div>

            {/* Warning for positive result */}
            {data.possuiSinistro && (
              <div className="p-5 bg-red-500/10 border border-red-500/20 rounded-2xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-200">
                    <p className="font-semibold mb-1">Recomendação</p>
                    <p className="text-red-300/80">
                      Veículos com registro de perda total podem ter sido reparados, mas 
                      podem apresentar problemas estruturais ou de segurança. Recomendamos 
                      uma vistoria técnica detalhada antes de qualquer negociação.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

export default ConsultaSinistroPTResult;

